import React, { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { images as imageApi } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

const CoinDetailModal = ({
  coin,
  isShared,
  setInfo,
  onClose,
  onImageClick,
  onRemoveFromSet,
  setId
}) => {
  const navigate = useNavigate();

  const handlePopState = useCallback((e) => {
    if (e.state?.modal !== 'coin') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-16 bottom-0 left-0 right-0 bg-black bg-opacity-75 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div className="h-full px-4 text-center">
        {/* This element is to trick the browser into centering the modal contents. */}
        <span
          className="inline-block h-full align-middle"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className="inline-block w-full max-w-4xl my-8 text-left align-middle transition-all transform bg-white rounded-lg shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-start">
              <h2 className="text-2xl font-bold text-gray-900">
                {coin.country} {coin.year}{coin.mint_mark ? `-${coin.mint_mark}` : ''} {coin.assayer} {coin.denomination} {coin.variety}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
            {/* Images Grid */}
            <div className="px-4 sm:px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                {(coin.images || []).map((image, index) => (
                  <div 
                    key={index}
                    className="relative bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
                    style={{ paddingTop: '100%' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onImageClick(index);
                    }}
                  >
                    <img
                      src={imageApi.getImage(image.file_path, image.source)}
                      alt={`${coin.year} ${coin.denomination} - Image ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-contain p-4"
                    />
                    {image.file_path === setInfo?.coverImagePath && (
                      <div className="absolute top-2 right-2 z-[5] bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">
                        Cover Image
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Coin Details - Sticky */}
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {(setInfo?.field_preferences?.visible_fields || []).map(field => {
                  const value = field === 'certification_service_id' ? coin.certification_service_name : coin[field];
                  if (!value) return null;
                  
                  let displayValue = value;
                  if (field === 'purchase_date') {
                    displayValue = new Date(value).toLocaleDateString();
                  } else if (field.includes('price') && !isNaN(value)) {
                    displayValue = `$${value.toLocaleString()}`;
                  } else if (field === 'weight') {
                    displayValue = `${value}g`;
                  } else if (field === 'diameter') {
                    displayValue = `${value}mm`;
                  } else if (field === 'mintage' && !isNaN(value)) {
                    displayValue = Number(value).toLocaleString();
                  } else if (field === 'grade' && coin.cac_sticker && coin.cac_sticker !== 'none') {
                    displayValue = (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                          coin.cac_sticker === 'gold' 
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            : 'bg-green-100 text-green-800 border border-green-200'
                        }`}>
                          CAC
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div key={field} className="min-w-[200px]">
                      <dt className="text-sm font-medium text-gray-500">
                        {field === 'certification_service_id' ? 'TPG' : 
                         field === 'certification_number' ? 'Cert #' :
                         field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{displayValue}</dd>
                    </div>
                  );
                })}
              </dl>

              {/* Notes and Details */}
              {setInfo?.field_preferences?.visible_fields?.includes('notes') && coin.notes && (
                <div className="mt-6">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Notes</dt>
                  <dd className="text-sm text-gray-900 bg-gray-100 rounded-lg p-4 whitespace-pre-wrap">{coin.notes}</dd>
                </div>
              )}

              {setInfo?.field_preferences?.visible_fields?.includes('coin_details') && coin.coin_details && (
                <div className="mt-6">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Details</dt>
                  <dd className="text-sm text-gray-900">
                    <div 
                      className="prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-ul:pl-5 prose-ol:pl-5 prose-li:my-0 prose-p:my-0 bg-gray-100 rounded-lg p-4"
                      dangerouslySetInnerHTML={{ __html: coin.coin_details }}
                    />
                  </dd>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {!isShared && (
            <div className="px-4 sm:px-6 py-4 bg-white border-t border-gray-200 flex-shrink-0">
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => navigate(`/coins/${coin.id}`)}
                  className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700"
                >
                  View Coin Page
                </button>
                {setInfo?.type === 'Static' && (
                  <button
                    onClick={async () => {
                      await onRemoveFromSet(setId, coin.id);
                      onClose();
                    }}
                    className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
                  >
                    Remove from Set
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CoinDetailModal;
